import type { AuditAction, ClientRole, StudioRole } from '@/api/types';

// What each role reaches (MK42-5783). The BFF enforces it; the UI only hides
// what would answer 403, so people do not click into dead ends.

export type ClientArea = 'work' | 'money' | 'documents' | 'admin';

const CLIENT: Record<ClientRole, readonly ClientArea[]> = {
  owner: ['work', 'money', 'documents', 'admin'],
  member: ['work', 'documents'],
  accountant: ['money', 'documents'],
};

/** Unknown role (workspace still loading) reaches nothing: role-gated queries wait for it. */
export const clientCan = (role: ClientRole | undefined, area: ClientArea) => !!role && CLIENT[role].includes(area);

export type ClientSection = 'home' | 'inbox' | 'money' | 'proposals' | 'documents' | 'access';

/** The client's top navigation for a role, in display order. */
export function clientSections(role: ClientRole | undefined): ClientSection[] {
  const work = clientCan(role, 'work');
  return [
    ...(work ? (['home', 'inbox'] as const) : []),
    ...(clientCan(role, 'money') ? (['money'] as const) : []),
    // The proposals endpoints answer to the same roles as the work areas: owner and member.
    ...(work ? (['proposals'] as const) : []),
    'documents',
    ...(work ? (['access'] as const) : []),
  ];
}

/** Where a client lands when the page they opened is not for their role; null = stay. */
export function clientRedirect(role: ClientRole | undefined, pathname: string): string | null {
  if (!role) return null;
  const work = pathname === '/' || /^\/(p|inbox|proposals|access)(\/|$)/.test(pathname);
  if (work && !clientCan(role, 'work')) return '/money';
  if (/^\/money(\/|$)/.test(pathname) && !clientCan(role, 'money')) return '/';
  return null;
}

export type StudioArea = 'clients' | 'money' | 'team';

const STUDIO: Record<StudioRole, readonly StudioArea[]> = {
  owner: ['clients', 'money', 'team'],
  manager: ['clients', 'money'],
  specialist: [],
};

export const studioCan = (role: StudioRole | undefined, area: StudioArea) => STUDIO[role ?? 'specialist'].includes(area);

export const studioRoleLabel: Record<StudioRole, string> = {
  owner: 'Владелец',
  manager: 'Менеджер',
  specialist: 'Специалист',
};

/** Shown instead of the owner-only buttons (accept, decline, renew) to everyone else. */
export const OWNER_DECIDES = 'Решает владелец компании';

export const clientRoleLabel: Record<ClientRole, string> = {
  owner: 'Владелец',
  member: 'Сотрудник',
  accountant: 'Бухгалтер',
};

export const auditLabel: Record<AuditAction, string> = {
  login: 'вошёл в кабинет',
  logout: 'вышел',
  invite: 'пригласил',
  role_changed: 'сменил роль',
  disabled: 'отключил доступ',
  enabled: 'вернул доступ',
  sessions_revoked: 'завершил сессии',
  colleague_role_changed: 'сменил роль коллеги',
  colleague_disabled: 'отключил коллегу',
  colleague_enabled: 'вернул доступ коллеге',
  invite_conflict: 'приглашение не прошло: адрес занят',
  person_edited: 'исправил имя или почту',
  person_anonymised: 'обезличил человека',
  secret_opened: 'открыл доступ',
  notification_retried: 'отправил уведомление снова',
  offboarded: 'завершил работу с клиентом',
  act_signed: 'подписал акт',
  document_signed: 'подписал документ',
  sign_request_created: 'отправил документ на подпись',
  payment_problem_resolved: 'разобрал проблему с оплатой',
  client_created: 'завёл клиента',
  client_updated: 'изменил данные клиента',
  invoice_issued: 'выставил счёт',
  invoice_reminded: 'напомнил об оплате',
  invoice_cancelled: 'отменил счёт',
  retainer_invoiced: 'выставил счёт по регулярным платежам',
  statement_added: 'добавил строку выписки',
  statement_matched: 'сопоставил выписку со счётом',
  statement_unmatched: 'отменил сопоставление выписки',
  statement_ignored: 'скрыл строку выписки',
  statement_imported: 'загрузил выписку',
  act_reissued: 'выпустил акт заново',
  project_created: 'создал проект',
  project_updated: 'изменил проект',
  project_linear_linked: 'связал проект с Linear',
  project_deleted: 'удалил проект',
  project_linear_changed: 'сменил проект Linear',
  project_linear_unlinked: 'отвязал проект от Linear',
  rhythm_done: 'отметил регулярную работу сделанной',
  contact_added: 'добавил контакт клиента',
  tasks_visibility: 'сменил видимость задач',
  project_renewed: 'продлил регулярные платежи',
  now_updated: 'обновил «Сейчас»',
  proposal_created: 'отправил предложение',
  proposal_drafted: 'подготовил черновик предложения',
  proposal_sent: 'отправил предложение',
  proposal_withdrawn: 'отозвал предложение',
  proposal_lost: 'отметил предложение проигранным',
  proposal_accepted: 'принял предложение',
  proposal_declined: 'отклонил предложение',
  change_requested: 'прислал идею или запрос',
  change_estimated: 'оценил запрос на изменение',
  change_decided: 'решил по оценке изменения',
  change_declined: 'отклонил запрос на изменение',
  change_asked: 'задал вопрос по запросу клиента',
  change_answered: 'ответил на вопрос по запросу',
  change_parked: 'отложил запрос клиента',
  change_reinvoiced: 'выставил счёт по запросу заново',
  secret_sent: 'передал доступ',
  secret_revoked: 'отозвал доступ',
  secret_rerequested: 'попросил передать доступ заново',
  document_uploaded: 'загрузил файл',
  task_visibility: 'сменил видимость задачи',
  task_needs_set: 'попросил клиента по задаче',
  task_needs_cleared: 'снял запрос по задаче',
  task_approved: 'утвердил задачу',
  report_published: 'опубликовал отчёт',
  report_updated: 'обновил отчёт',
  report_drafted: 'подготовил черновик отчёта',
  report_draft_updated: 'обновил черновик отчёта',
  thread_closed: 'закрыл обсуждение',
  thread_created: 'начал обсуждение',
  meeting_created: 'назначил встречу',
  meeting_updated: 'изменил встречу',
  meeting_canceled: 'отменил встречу',
  onboarding_toggled: 'отметил пункт запуска',
  onboarding_completed: 'выполнил пункт запуска',
  payer_saved: 'сохранил реквизиты плательщика',
  checkout_started: 'начал оплату',
  transfer_reported: 'сообщил о переводе',
  profile_updated: 'обновил свой профиль',
  invoice_draft_saved: 'сохранил черновик счёта',
  invoice_draft_updated: 'изменил черновик счёта',
  invoice_draft_deleted: 'удалил черновик счёта',
  invoice_sent: 'отправил счёт клиенту',
  act_drafted: 'составил черновик акта',
  act_updated: 'изменил черновик акта',
  act_deleted: 'удалил черновик акта',
  act_sent: 'отправил акт на подпись',
  act_voided: 'отозвал акт',
  act_marked_signed: 'отметил акт подписанным вне кабинета',
  act_scan_uploaded: 'загрузил подписанный скан акта',
};

/** What the portal does with a secret today (MK42-5821 step 1). Must stay true: the key travels with the ciphertext until step 2. */
export const SECRET_NOTICE =
  'Значение шифруется в браузере и хранится в кабинете в зашифрованном виде до первого открытия или истечения срока; после этого стирается. Студия технически может его прочитать — не передавайте через кабинет то, что студии знать нельзя.';
